import {
    DataTexture,
    FloatType,
    LinearFilter,
    Mesh,
    MeshBasicMaterial,
    OrthographicCamera,
    PlaneBufferGeometry,
    RGBAFormat,
    Scene,
    Texture,
    TextureLoader,
    VideoTexture,
    WebGLRenderer,
} from 'three';

import { CopyPass, EffectComposer, RenderPass, ShaderPass, } from 'postprocessing';

import { CurveMaterial, LiftGammaGainMaterial, LUT3DMaterial } from '@/assets/js/materials';

import { TIFFLoader } from '@/assets/js/loaders/TIFFLoader';
import { EXRLoader } from 'three/examples/jsm/loaders/EXRLoader';
import { generateUniqueId } from '@/assets/js/Utilities';

export default class GPU {
    constructor() {
        this.scene = new Scene();
        this.renderer = new WebGLRenderer({
            powerPreference: 'high-performance',
            preserveDrawingBuffer: true,
            antialias: false,
            stencil: false,
            depth: false,
        });
        // this.renderer.setPixelRatio(window.devicePixelRatio);
        this.renderer.domElement.id = 'main-canvas';
        document.querySelector('#canvases').prepend(this.renderer.domElement);

        this.renderer.domElement.addEventListener('webglcontextlost', function(event) {
            event.preventDefault();
            vm.$snackbar(`No WebGL available. Please reload the page and if the error persists, restart the browser.`, 'error', {
                class: 'browser-crash-notice',
                timeout: -1,
            });
        });

        this.exportRenderer = new WebGLRenderer({
            powerPreference: 'high-performance',
            preserveDrawingBuffer: true,
            depth: false,
            stencil: false,
            antialias: false,
        });
        this.exportRenderer.domElement.id = 'export-canvas';
        document.querySelector('#canvases').append(this.exportRenderer.domElement);

        this.composer = new EffectComposer(this.renderer, {
            frameBufferType: FloatType,
        });

        this.composer.inputBuffer.name = 'InputBuffer';
        this.composer.outputBuffer.name = 'OutputBuffer';

        this.geometry = new PlaneBufferGeometry(window.innerWidth, window.innerHeight);
        this.camera = new OrthographicCamera(
            window.innerWidth / -2,
            window.innerWidth / 2,
            window.innerHeight / 2,
            window.innerHeight / -2
        );

        this.THREEtextureLoader = new TextureLoader();
        this.EXRLoader = (new EXRLoader()).setDataType(FloatType);
        this.TIFFLoader = new TIFFLoader();
        this.textureLoader = this.THREEtextureLoader;

        this.canvasRatioX = 1;
        this.canvasRatioY = 1;

        this.thumbnails = {
            scene: new Scene(),
            camera: new OrthographicCamera(window.innerWidth / -2, window.innerWidth / 2, window.innerHeight / 2, window.innerHeight / -2),
        };
    }

    swapTextureLoader() {
        let type = loader.activeMedia.rawDevelopment.type || loader.activeMedia.type;
        if (loader.isEXRFile(type)) this.textureLoader = this.EXRLoader;
        else if (loader.isTiffFile(type)) this.textureLoader = this.TIFFLoader;
        else this.textureLoader = this.THREEtextureLoader;
    }

    setThumbnailRenderer(canvas) {
        this.thumbnails.renderer = new WebGLRenderer({
            preserveDrawingBuffer: true,
            powerPreference: 'high-performance',
            canvas: canvas,
            depthTest: false,
            depth: true,
            antialias: false,
        });

        this.thumbnails.composer = new EffectComposer(this.thumbnails.renderer);
    }

    getDrawingBufferSize(renderer) {
        return {
            width: renderer.getContext().drawingBufferWidth,
            height: renderer.getContext().drawingBufferHeight,
        };
    }

    zoomCanvas(zoomValue) {
        let currentZoom = this.camera.zoom,
            currentPosX = this.camera.position.x,
            currentPosY = this.camera.position.y,
            leftOffset,
            topOffset,
            newZoom,
            stepsLeft;

        // NOTE do we need this block?
        if (zoomValue < 0 && (currentPosX != 0 || currentPosY != 0)) {
            stepsLeft = parseInt((currentZoom - 1) / zoomValue);
            if (stepsLeft == 0) {
                if (this.camera.zoom > 1) {
                    this.camera.position.x = 0;
                    this.camera.position.y = 0;
                }
            } else {
                leftOffset = currentPosX / stepsLeft;
                topOffset = currentPosY / stepsLeft;
                this.camera.position.x = currentPosX + leftOffset;
                this.camera.position.y = currentPosY + topOffset;
            }
        }

        newZoom = parseFloat(currentZoom) + parseFloat(zoomValue);

        // Max and min zoom level
        if (newZoom > 3 || newZoom < 1) return;

        this.camera.zoom = newZoom;
        this.camera.updateProjectionMatrix();
        this.composer.render();
    }

    moveScene(newPos) {
        this.camera.position.x = newPos.x;
        this.camera.position.y = newPos.y;
        this.camera.updateProjectionMatrix();
        this.composer.render();
    }

    copyPasses(originComposer, targetComposer) {
        targetComposer.passes = originComposer.passes;
    }

    swapComposerRenderer(renderer) {
        if (renderer == 'export') renderer = this.exportRenderer;
        else if (renderer == 'thumbnail') renderer = this.thumbnails.renderer;
        // else if (renderer == 'colour-match') renderer = this.colourMatchRenderer;
        else renderer = this.renderer;

        this.composer.setRenderer(renderer);
    }

    createRenderPass(enabled = false) {
        let renderPass = new RenderPass(this.scene, this.camera);
        renderPass.enabled = enabled ? true : false;
        // renderPass.renderToScreen = false;
        renderPass.uniforms = {
            opacity: { value: 1 },
            blendID: { value: 0 },
        };
        renderPass.uid = generateUniqueId();

        return renderPass;
    }

    createLUTPass(texture) {
        const pass = new ShaderPass(new LUT3DMaterial(texture));
        pass.name = 'LUT3DPass';
        pass.uniforms = pass.fullscreenMaterial.uniforms;
        pass.uid = generateUniqueId();

        return pass;
    }

    createCurveShaderPass(name, enabled) {
        let texture = this.createCurveTexture();
        let pass = new ShaderPass(new CurveMaterial(texture));
        pass.uniforms = pass.fullscreenMaterial.uniforms;
        pass.enabled = enabled ? true : false;
        pass.name = name ? name : 'CurvePass';
        pass.uid = generateUniqueId();

        return pass;
    }

    createLiftGammaGainPass() {
        const pass = new ShaderPass(new LiftGammaGainMaterial());
        pass.name = 'LiftGammaGainPass';
        pass.uniforms = pass.fullscreenMaterial.uniforms;
        pass.uid = generateUniqueId();

        return pass;
    }

    addPassToComposer(pass, toMiddle = false) {
        if (toMiddle) this.composer.addPass(pass, this.getLastDynamicPassIndex());
        else this.composer.addPass(pass);
    }

    getLastDynamicPassIndex() {
        return this.composer.passes.length - config.endFixedPasses;
    }

    getLastBlockPass() {
        return this.composer.passes[this.composer.passes.length - 1 - config.endFixedPasses];
    }

    getLastPass() {
        return this.composer.passes[this.composer.passes.length - 1];
    }

    setLastPass_renderToScreen(option = false) {
        this.composer.passes[this.composer.passes.length - 1].renderToScreen = option;
    }

    disableAllCreativePasses(render = true) {
        if (gpu.composer) {
            gpu.composer.passes.forEach((pass, key) => {
                if (key == 0) {
                    // pass.renderToScreen = true;
                    return;
                }
                // pass.enabled = false;
            });

            if (render) gpu.render();
        }
    }

    render() {
        this.composer.render();
    }

    removePass(uid) {
        this.composer.passes.forEach((pass, i) => {
            if (pass.uid == uid) this.composer.passes.splice(i, 1);
        });
    }

    getPassIndexById(uid) {
        let passIndex = null;
        this.composer.passes.forEach((pass, i) => {
            if (pass.uid == uid) passIndex = i;
        });
        return passIndex;
    }

    loadVideoTexture() {
        return new Promise(resolve => {
            let videoEl = document.createElement('video');
            videoEl.setAttribute('id', 'video');
            videoEl.classList.add('hide');
            let interval;

            // because we wait for width and height of video to be fully loaded
            videoEl.addEventListener(
                'loadedmetadata',
                () => {
                    let video = document.getElementById('video');

                    vm.$store.dispatch('UI/setVideoLoaded', true);
                    vm.$store.dispatch('UI/setVideoDuration', video.duration);

                    let texture = new VideoTexture(video);
                    texture.image.width = texture.image.videoWidth;
                    texture.image.height = texture.image.videoHeight;

                    // vm.$store.dispatch("Helpers/revokeObjectURL", loader.activeVideo);

                    resolve(texture);
                },
                false
            );

            videoEl.addEventListener('timeupdate', e => {
                vm.$store.dispatch('UI/setVideoCurrentTime', e.target.currentTime);
            });

            videoEl.addEventListener('ended', e => {
                vm.$store.dispatch('UI/setVideoPlaying');
            });

            videoEl.addEventListener('seeked', e => {
                gpu.render();
            });

            videoEl.addEventListener('playing', e => {
                if (!interval) {
                    interval = setInterval(() => {
                        gpu.render();
                    }, 40);
                }
            });

            videoEl.addEventListener('pause', e => {
                clearInterval(interval);
                interval = undefined;
            });

            let sourceEl = document.createElement('source');
            sourceEl.type = 'video/mp4';
            videoEl.append(sourceEl);

            // videoEl.addEventListener("ended", e => {
            // 	clearInterval(interval);
            // 	console.log("interval cleard");
            // });

            // videoEl.addEventListener("abort", e => {
            // 	clearInterval(interval);
            // 	console.log("interval cleard");
            // });

            // let videoSource = document.getElementById("video-source");
            UI.container.mainCanvases.append(videoEl);
            sourceEl.src = loader.activeMedia.url;
        });
    }

    getActiveMediaTexture() {
        return new Promise(resolve => {
            if (loader.typeOfActiveMedia('video')) {
                resolve(this.loadVideoTexture());
            }

            if (loader.activeMedia.rawDevelopment.imageData) {
                let texture = new Texture(loader.activeMedia.rawDevelopment.imageData);
                resolve(texture);
            }

            this.swapTextureLoader();

            let activeMediaURL = loader.activeMedia.rawDevelopment.url || loader.activeMedia.url;
            this.textureLoader.load(activeMediaURL, texture => {
                resolve(texture);
            });
        });
    }

    async initThreejs() {
        loader.activeMedia.THREE.texture = await this.getActiveMediaTexture();
        let textureLoaded = loader.activeMedia.THREE.texture;

        lutify.setImageData(textureLoaded.image.width, textureLoaded.image.height);

        lutify.checkImageSize();
        UI.canvas.setSizeCSS();

        this.renderer.setSize(lutify.imageWidth, lutify.imageHeight, false);
        this.composer.setSize(lutify.imageWidth, lutify.imageHeight);

        this.canvasRatioX = window.innerWidth / UI.canvas.$el.width();
        this.canvasRatioY = window.innerHeight / UI.canvas.$el.height();

        // NOTE do we need to set the properties of texture?
        this.texture = textureLoaded;
        this.texture.generateMipmaps = false;
        this.texture.magFilter = LinearFilter;
        this.texture.minFilter = LinearFilter;
        this.texture.needsUpdate = true;

        // let type = loader.activeMedia.rawDevelopment.type || loader.activeMedia.type;
        // if (!loader.isTiffFile(type)) this.texture.type = FloatType;

        this.mesh = new Mesh(
            this.geometry,
            new MeshBasicMaterial({
                map: this.texture
            })
        );

        this.thumbnails.mesh = new Mesh(
            this.geometry,
            new MeshBasicMaterial({
                map: this.texture
            })
        );

        this.mesh.position.y = -0;
        this.mesh.position.z = -300;
        this.thumbnails.mesh.position.y = -0;
        this.thumbnails.mesh.position.z = -300;

        // SCENE
        this.scene.add(this.mesh);
        this.thumbnails.scene.add(this.thumbnails.mesh);

        // PASSES
        this.addInitialPasses();

        // PREVIEWS
        lutPreviewer.init(this.texture);

        this.render();
        UI.canvas.$el.css('opacity', 1);

        this.setExportRendererSize();
    }

    async initNewImage() {
        loader.activeMedia.THREE.texture = await this.getActiveMediaTexture();
        let textureLoaded = loader.activeMedia.THREE.texture;

        if (!textureLoaded) return; // when switching raw files fast
        lutify.setImageData(textureLoaded.image.width, textureLoaded.image.height);
        lutify.checkImageSize();
        UI.canvas.setSizeCSS();
        this.renderer.setSize(lutify.imageWidth, lutify.imageHeight, false);
        this.composer.setSize(lutify.imageWidth, lutify.imageHeight);

        this.canvasRatioX = window.innerWidth / UI.canvas.$el.width();
        this.canvasRatioY = window.innerHeight / UI.canvas.$el.height();

        this.texture = textureLoaded;
        this.texture.generateMipmaps = false;
        this.texture.magFilter = LinearFilter;
        this.texture.minFilter = LinearFilter;
        this.texture.needsUpdate = true;

        this.camera.zoom = 1;
        this.camera.updateProjectionMatrix();

        this.scene.children[0].material.map = this.texture;
        this.moveScene({ x: 0, y: 0 });
        this.render();

        lutPreviewer.setSize();
        UI.lutPreviewThumbnail.setSize(lutPreviewer.width, lutPreviewer.height);

        this.setExportRendererSize();

        if (lutify.splitViewActive) lutify.removeSplitView();
    }

    setExportRendererSize() {
        let w = lutify.imageWidth;
        let h = lutify.imageHeight;

        if (lutify.canSaveFullSizeImage()) {
            let originalWidth = lutify.originalImageSize.width;
            let originalHeight = lutify.originalImageSize.height;
            UI.exportCanvas.setSize(originalWidth, originalHeight);

            if (navigator.userAgent.toLowerCase().indexOf('firefox') > -1) {
                let maxTextureSize = this.exportRenderer.capabilities.maxTextureSize;
                if (originalWidth > maxTextureSize || originalHeight > maxTextureSize) {
                    let ratio = originalWidth / originalHeight;

                    if (originalWidth > originalHeight) {
                        UI.exportCanvas.setSize(maxTextureSize, maxTextureSize / ratio);
                    } else {
                        UI.exportCanvas.setSize(maxTextureSize * ratio, maxTextureSize);
                    }
                }
            }

            let drawingBufferSize = this.getDrawingBufferSize(this.exportRenderer);
            let ratio = drawingBufferSize.width / drawingBufferSize.height;

            if (drawingBufferSize.width > drawingBufferSize.height) {
                w = drawingBufferSize.width;
                h = w / ratio;
            } else {
                h = drawingBufferSize.height;
                w = h * ratio;
            }
        }

        this.exportRenderer.setSize(w, h);
    }

    getTexture(objectURL) {
        return new Promise(resolve => {
            this.textureLoader.load(objectURL, textureLoaded => {
                vm.$store.dispatch('Helpers/revokeObjectURL', objectURL);

                this.thumbnails.texture = textureLoaded;
                this.thumbnails.texture.generateMipmaps = false;
                this.thumbnails.texture.magFilter = LinearFilter;
                this.thumbnails.texture.minFilter = LinearFilter;
                this.thumbnails.texture.type = FloatType;
                this.thumbnails.texture.needsUpdate = true;
                resolve(this.thumbnails.texture);
            });
        });
    }

    resetCamera() {
        this.camera.zoom = 1;
        this.camera.updateProjectionMatrix();
        this.moveScene({ x: 0, y: 0 });
    }

    addInitialPasses() {
        // RENDER PASS
        let renderPass = this.createRenderPass('enabled');
        this.addPassToComposer(renderPass);
        blocks.addFixed({ passes: [renderPass], name: 'RENDER BLOCK' });

        const copyPass = new CopyPass();
        this.addPassToComposer(copyPass);
        blocks.addFixed({ passes: [copyPass], name: 'COPY BLOCK' });
    }

    disableDynamicPasses() {
        lutify.blocks.forEach(block => {
            if (block.type == 'dynamic') {
                block.passes.forEach(pass => (pass.enabled = false));
            }
        });
    }

    enableDynamicPasses() {
        lutify.blocks.forEach(block => {
            if (block.type == 'dynamic') {
                block.passes.forEach(pass => (pass.enabled = true));
            }
        });
    }

    // Turns the changes off
    hideDynamicPasses() {
        let newPasses = [];

        lutify.blocks.forEach(block => {
            if (block.type == 'fixed') {
                block.passes.forEach(pass => newPasses.push(pass));
            }
        });

        lutify.tempPasses = this.composer.passes;
        this.composer.passes = newPasses;
    }

    getFixedPasses() {
        return lutify.blocks.filter(block => block.type == 'fixed').map(block => block.passes[0]);
    }

    getOriginalImageSrc() {
        this.hideDynamicPasses();
        this.render();
        let originalImage = UI.canvas.$el[0].toDataURL();

        this.composer.passes = lutify.tempPasses;
        this.render();

        delete lutify.tempPasses;
        return originalImage;
    }

    getThumbnailsCanvasSrc() {
        return UI.thumbnailCanvas.el.toDataURL();
    }

    pointsToCurve(points) {
        var array = [];
        for (var i = 0; i < 1024; i++) {
            array.splice(array.length, 0, points[i], points[i], points[i], 1);
            // array.splice(array.length, 0, red[i], green[i], blue[i], 255);
        }

        // var finalArray = new Uint8Array(array);
        var finalArray = new Float32Array(array);
        var curveTexture = new DataTexture(finalArray, 1024, 1);

        curveTexture.format = RGBAFormat;
        curveTexture.type = FloatType;
        curveTexture.needsUpdate = true;
        curveTexture.flipY = true;

        return curveTexture;
    }

    RGBPointsToCurve(pointsRed, pointsGreen, pointsBlue) {
        var array = [];

        for (let i = 0; i < 1024; i++) {
            array.splice(array.length, 0, pointsRed[i], pointsGreen[i], pointsBlue[i], 1);
        }

        // let finalArray = new Uint8Array(array);
        let finalArray = new Float32Array(array);
        let curveTexture = new DataTexture(finalArray, 1024, 1);

        curveTexture.format = RGBAFormat;
        curveTexture.type = FloatType;
        curveTexture.needsUpdate = true;
        curveTexture.flipY = true;

        return curveTexture;
    }

    createCurveTexture() {
        let array = [];
        for (let i = 0; i < 1024; i++) {
            // array.splice(array.length, 0, i, i, i, 255);
            array.splice(array.length, 0, i / 1023, i / 1023, i / 1023, 1);
        }

        // let startArray = new Uint8Array(array);
        let startArray = new Float32Array(array);
        let curveTexture = new DataTexture(startArray, 1024, 1);
        curveTexture.format = RGBAFormat;
        curveTexture.type = FloatType;
        curveTexture.needsUpdate = true;
        curveTexture.flipY = true;

        return curveTexture;
    }

    async getExportData(lutSize, exportType = false, data = false) {
        let lutIdentity = await luts.load([luts[`identity${lutSize}`]]);
        lutIdentity = lutIdentity[0].texture.toDataTexture();
        let w = lutIdentity.image.width;
        let h = lutIdentity.image.height;
        let origTexture = gpu.scene.children[0].material.map;

        // scene.children[1] is the mesh, which contains the actual image rendered on canvas.
        // We are now replacing this material texture with new texture, which is the image of identity LUT.
        // We then render the identity LUT image to the canvas, and all changes (as passes) are applied to it.
        // After that, we will read each pixels color values and store them into a buffer which will then be used as values in the exported file.
        // At the end we will re-asign the value of scene.children[0] to it's original texture, and re-render again
        gpu.scene.children[0].material.map = lutIdentity;
        gpu.scene.children[0].scale.x = w / gpu.scene.children[0].geometry.parameters.width;
        gpu.scene.children[0].scale.y = h / gpu.scene.children[0].geometry.parameters.height;

        let cameraZoom = gpu.camera.zoom;
        let cameraPositionX = gpu.camera.position.x;
        let cameraPositionY = gpu.camera.position.y;
        gpu.camera.zoom = 1;
        gpu.camera.position.x = 0;
        gpu.camera.position.y = 0;

        gpu.camera.left = w / -2;
        gpu.camera.right = w / 2;
        gpu.camera.top = h / 2;
        gpu.camera.bottom = h / -2;
        gpu.camera.updateProjectionMatrix();

        // Saving the values - width, height - of the original texture image
        let tw = gpu.composer.inputBuffer.width,
            th = gpu.composer.inputBuffer.height;

        // gpu.renderer.setPixelRatio(1);
        gpu.renderer.setSize(w, h, false);
        gpu.composer.setSize(w, h);

        // gpu.composer.passes[0].clear = false;
        let originalPasses = gpu.composer.passes;
        let newPasses = [...originalPasses];

        gpu.renderer.clear();

        let blocksData = _.cloneDeep(lutify.getRawBlocksData());
        blocksData = blocksData.filter(block => block.name !== 'midtoneTexture'); // filtering out texture block
        newPasses = newPasses.filter(pass => pass.name !== 'DitherPass');

        gpu.composer.passes = newPasses;
        gpu.render();

        blocksData = blocksData.splice(1); // render block

        // Reading pixels from the buffer
        let pixels = new Float32Array(w * h * 4);

        const copyPass = new CopyPass();
        gpu.composer.addPass(copyPass);
        copyPass.renderToScreen = false; // important because otherwise the renderTarget is set to null

        gpu.render();
        gpu.renderer.readRenderTargetPixels(gpu.composer.renderer.getRenderTarget(), 0, 0, w, h, pixels);

        // Now we are restoring the scene.children[1] with the original image texture and re-rendering it to the canvas
        gpu.renderer.setPixelRatio(1);
        gpu.renderer.setSize(lutify.imageWidth, lutify.imageHeight, false);
        gpu.composer.setSize(tw, th, false);

        w = gpu.scene.children[0].geometry.parameters.width;
        h = gpu.scene.children[0].geometry.parameters.height;

        gpu.camera.zoom = cameraZoom;
        gpu.camera.position.x = cameraPositionX;
        gpu.camera.position.y = cameraPositionY;

        gpu.camera.left = w / -2;
        gpu.camera.right = w / 2;
        gpu.camera.top = h / 2;
        gpu.camera.bottom = h / -2;
        gpu.camera.updateProjectionMatrix();

        gpu.scene.children[0].scale.x = gpu.scene.children[0].scale.y = 1;
        gpu.scene.children[0].material.map = origTexture;

        gpu.composer.passes = originalPasses;
        gpu.composer.passes[gpu.composer.passes.length - 1].renderToScreen = true;
        gpu.render();

        return { pixels, blocksData };
    }

    isRunningOnM1() {
        let canvas = document.createElement('canvas');
        let gl;
        var debugInfo;
        var renderer;

        try {
            gl = canvas.getContext('webgl') || canvas.getContext('experimental-webgl');
        } catch (e) {
            console.log(e);
        }

        if (gl) {
            debugInfo = gl.getExtension('WEBGL_debug_renderer_info');
            renderer = gl.getParameter(debugInfo.UNMASKED_RENDERER_WEBGL);
            if (renderer.indexOf('M1') == -1) return true;
        }
    }
}
